import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Megaphone, PaperPlaneTilt, Eye, Warning, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const TARGETS = [
  { id: "expiring_30", label: "Expiring in 30 days", hint: "Best for early-renewal nudges" },
  { id: "expiring_7", label: "Expiring in 7 days", hint: "Last-call reminders" },
  { id: "all", label: "All active plans", hint: "Every customer with an active plan" },
  { id: "expired_recent", label: "Recently expired (60d)", hint: "Win-back campaigns" },
];

const TEMPLATES = [
  {
    label: "30-day renewal nudge",
    subject: "Your maintenance plan renews soon",
    body: "Hi there,\n\nQuick heads-up — your Vision Grafix maintenance plan is up for renewal in the next 30 days. To keep enjoying free website updates, hop into your dashboard and choose a renewal option.\n\nLet us know if you have questions.",
  },
  {
    label: "Final 7-day reminder",
    subject: "Last call: your plan expires in 7 days",
    body: "Heads up — your Vision Grafix maintenance plan expires in just under a week.\n\nIf you'd like uninterrupted free website updates, please renew from your dashboard before then. We're here to help if anything looks off.",
  },
  {
    label: "Win-back (recently expired)",
    subject: "Welcome back — reactivate your maintenance plan",
    body: "We noticed your Vision Grafix maintenance plan recently expired.\n\nIf you'd like to reactivate it for another 12 months of free updates, just reply to this email or hit the button below to manage your plan.",
  },
];

export default function AdminMaintenanceBroadcast() {
  const [target, setTarget] = useState("expiring_30");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const applyTemplate = (t) => {
    setSubject(t.subject);
    setBody(t.body);
  };

  const doPreview = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Add a subject and body first");
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/maintenance/broadcast", {
        target, subject, body, dry_run: true,
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not preview");
    } finally {
      setPreviewing(false);
    }
  };

  const doSend = async () => {
    if (!preview || preview.recipient_count === 0) {
      toast.error("Run preview first to confirm recipients");
      return;
    }
    if (!window.confirm(`Send to ${preview.recipient_count} recipient${preview.recipient_count === 1 ? "" : "s"}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/maintenance/broadcast", {
        target, subject, body, dry_run: false,
      });
      setResult(data);
      toast.success(`Queued ${data.queued} email${data.queued === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Maintenance</div>
          <h1 className="font-display text-5xl mt-2">Broadcast</h1>
          <p className="text-[#a3a3a3] mt-2 text-sm max-w-2xl">Send a one-off email to a filtered slice of your maintenance-plan customers — perfect for renewal nudges or win-back campaigns. The daily auto-cron handles routine 30/7-day reminders; this is for everything else.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose column */}
        <div className="lg:col-span-2 space-y-5">
          {/* Target */}
          <div className="border border-[#262626] bg-[#0a0a0a] p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D] mb-3">1. Pick audience</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TARGETS.map((t) => (
                <label
                  key={t.id}
                  className={`flex items-start gap-2 p-3 border cursor-pointer transition-colors ${
                    target === t.id ? "border-[#FF2D2D] bg-[#FF2D2D]/5" : "border-[#262626]"
                  }`}
                >
                  <input
                    type="radio"
                    name="target"
                    value={t.id}
                    checked={target === t.id}
                    onChange={() => { setTarget(t.id); setPreview(null); setResult(null); }}
                    data-testid={`target-${t.id}`}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-semibold">{t.label}</div>
                    <div className="text-xs text-[#a3a3a3] mt-0.5">{t.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Templates */}
          <div className="border border-[#262626] bg-[#0a0a0a] p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D] mb-3">2. Start from a template <span className="normal-case tracking-normal text-[#a3a3a3] font-normal">(optional)</span></div>
            <div className="flex gap-2 flex-wrap">
              {TEMPLATES.map((t) => (
                <button
                  key={t.label}
                  onClick={() => applyTemplate(t)}
                  data-testid={`template-${t.label.replace(/\s+/g, "-")}`}
                  className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] text-[#a3a3a3] hover:border-[#FF2D2D] hover:text-[#FF2D2D]"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Compose */}
          <div className="border border-[#262626] bg-[#0a0a0a] p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D] mb-3">3. Write the message</div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => { setSubject(e.target.value); setPreview(null); setResult(null); }}
              data-testid="broadcast-subject"
              maxLength={120}
              placeholder="Renewal reminder"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-2.5 text-sm focus:border-[#FF2D2D] outline-none mb-4"
            />
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Body <span className="normal-case tracking-normal text-[#666]">(plain text — line breaks become paragraphs)</span></label>
            <textarea
              rows={8}
              value={body}
              onChange={(e) => { setBody(e.target.value); setPreview(null); setResult(null); }}
              data-testid="broadcast-body"
              placeholder="Hi there,&#10;&#10;Just a quick note about your plan…"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 text-sm focus:border-[#FF2D2D] outline-none"
            />
            <div className="text-xs text-[#a3a3a3] mt-2">Recipients automatically get a "Manage your plan" CTA pointing to their dashboard.</div>
          </div>

          {/* Send buttons */}
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={doPreview}
              disabled={previewing || !subject.trim() || !body.trim()}
              data-testid="broadcast-preview-btn"
              className="btn-outline px-5 py-3 text-sm uppercase tracking-wider flex items-center gap-2 disabled:opacity-40"
            >
              <Eye size={14} /> {previewing ? "Counting…" : "Preview audience"}
            </button>
            <button
              onClick={doSend}
              disabled={sending || !preview || preview.recipient_count === 0}
              data-testid="broadcast-send-btn"
              className="btn-primary px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 disabled:opacity-40"
            >
              <PaperPlaneTilt size={14} weight="bold" />
              {sending ? "Sending…" : preview ? `Send to ${preview.recipient_count} →` : "Preview first"}
            </button>
          </div>
        </div>

        {/* Right column: preview / result */}
        <div className="space-y-5">
          {preview && (
            <div className="border border-[#FF2D2D] bg-[#FF2D2D]/5 p-5" data-testid="broadcast-preview">
              <div className="flex items-center gap-2 mb-2">
                <Megaphone size={14} weight="fill" className="text-[#FF2D2D]" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Audience preview</div>
              </div>
              <div className="font-display text-3xl tabular-nums" data-testid="preview-count">{preview.recipient_count}</div>
              <div className="text-xs text-[#a3a3a3] mt-1">recipient{preview.recipient_count === 1 ? "" : "s"} match this filter</div>
              {preview.recipient_count === 0 ? (
                <div className="mt-4 p-3 border border-[#262626] text-xs text-[#a3a3a3] flex items-start gap-2">
                  <Warning size={12} className="text-[#a3a3a3] mt-0.5" />
                  No customers in this audience. Try a different filter.
                </div>
              ) : (
                <div className="mt-4 max-h-72 overflow-y-auto divide-y divide-[#FF2D2D]/20 text-xs">
                  {preview.recipients.map((r) => (
                    <div key={r.id} className="py-2">
                      <div className="font-semibold truncate">{r.name}</div>
                      <div className="text-[#a3a3a3] truncate">{r.email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {result && (
            <div className="border border-[#22c55e]/40 bg-[#22c55e]/5 p-5" data-testid="broadcast-result">
              <div className="flex items-center gap-2 mb-2">
                <Check size={14} weight="bold" className="text-[#22c55e]" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#22c55e]">Sent</div>
              </div>
              <div className="font-display text-3xl tabular-nums">{result.queued}</div>
              <div className="text-xs text-[#a3a3a3] mt-1">email{result.queued === 1 ? "" : "s"} queued for delivery</div>
              <div className="text-xs text-[#a3a3a3] mt-3">
                Check delivery status in <a href="/admin/email" className="text-[#FF2D2D] underline">Email diagnostics</a>.
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
